// @ts-check

import { memoize } from "lodash";

import type { DeviceInfo } from "@app/types/device";
import type { Features } from "@app/types/experiments";
import type {
  GeoDataCountry,
  GeoDataExtraBillingField,
  GeoDataFormattedRegion
} from "@app/types/geo-data";

function getTopBillingCountriesRanks(features: Partial<Features>) {
  return features.TOP_COUNTRIES_LIST ?? [];
}

// These countries are either ignored or coming from Napi
// Few are still missing we have countries that don't have flags ("legacy countries" such as Serbia and Montenegro that are 2 separated countries now)
const MISSING_FLAG_EMOJIS = [
  { iso3: "ATA", emoji: "U+1F1E6 U+1F1F6" },
  { iso3: "HMD", emoji: "U+1F1ED U+1F1F2" },
  { iso3: "BVT", emoji: "U+1F1E7 U+1F1FB" },
  { iso3: "ASM", emoji: "U+1F1E6 U+1F1F8" },
  { iso3: "CCK", emoji: "U+1F1E8 U+1F1E8" },
  { iso3: "CXR", emoji: "U+1F1E8 U+1F1FD" },
  { iso3: "ESH", emoji: "U+1F1EA U+1F1ED" },
  { iso3: "SGS", emoji: "U+1F1EC U+1F1F8" },
  { iso3: "GUM", emoji: "U+1F1EC U+1F1FA" },
  { iso3: "MNP", emoji: "U+1F1F2 U+1F1F5" },
  { iso3: "NFK", emoji: "U+1F1F3 U+1F1EB" },
  { iso3: "PRI", emoji: "U+1F1F5 U+1F1F7" },
  { iso3: "ATF", emoji: "U+1F1F9 U+1F1EB" },
  { iso3: "UMI", emoji: "U+1F1FA U+1F1F2" },
  { iso3: "VIR", emoji: "U+1F1FB U+1F1EE" },
  { iso3: "XKX", emoji: "U+1F1FD U+1F1F0" },
  { iso3: "PSE", emoji: "U+1F1F5 U+1F1F8" }
];

const COUNTRIES_ALLOWING_MARKETING_CHANNELS_IMPLICIT_CONSENT = [
  "AR",
  "AU",
  "CA",
  "US"
] as const;

const COUNTRIES_REQUIRING_MARKETING_CHANNELS_EXPLICIT_CONSENT = ["BR"] as const;

const COUNTRIES_WHERE_REGIONS_ARE_NAMED_PROVINCE = ["CA", "PE"];
const COUNTRIES_WHERE_REGIONS_ARE_NAMED_STATE = ["AU", "BR", "MX", "US"];

export type RegionType = "province" | "state";

export class GeoDataService {
  private countries: Array<{ name: string; value: string }> | undefined;
  private trunk_codes_map: { [iso3: string]: string } | undefined;
  private top_billing_countries:
    | Array<{ name: string; value: string }>
    | undefined;
  private flag_emojis_map: { [iso3: string]: string } | undefined;

  public constructor(
    public readonly locale: string,
    private readonly data: Array<GeoDataCountry>
  ) {
    this.getCountryByCode2 = memoize(this.getCountryByCode2);
    this.getCountryByCode3 = memoize(this.getCountryByCode3);
    this.getRegionsType = memoize(this.getRegionsType);
  }

  public getCountries(): Array<{ name: string; value: string }> {
    if (!this.countries) {
      this.countries = this.data.map(country => formatCountry(country));
    }

    return this.countries;
  }

  public getCountryByCode2(
    code2: string | undefined
  ): GeoDataCountry | undefined {
    return this.data.find(country => country.iso2 === code2);
  }

  public getCountryByCode3(
    code3: string | undefined
  ): GeoDataCountry | undefined {
    if (!code3) {
      return undefined;
    }
    return this.data.find(country => country.iso3 === code3);
  }

  public getRegions(code3: string): Array<GeoDataFormattedRegion> {
    const country = this.getCountryByCode3(code3);

    if (!country || !country.regions || !country.regions.list) {
      return [];
    }

    const regions = country.regions.list;

    const formatted_regions = regions.map(region => {
      return {
        name: region.name,
        value: region.iso2,
        zip_ranges: region.zip_ranges,
        zip_regex: region.zip_regex
      };
    });

    return formatted_regions;
  }

  public getRegionsType(code3: string): RegionType | null {
    const country = this.getCountryByCode3(code3);
    if (!country) {
      return null;
    }

    if (COUNTRIES_WHERE_REGIONS_ARE_NAMED_PROVINCE.includes(country.iso2)) {
      return "province";
    }
    if (COUNTRIES_WHERE_REGIONS_ARE_NAMED_STATE.includes(country.iso2)) {
      return "state";
    }
    return null;
  }

  public getZip(code3: string): string | null {
    const country = this.getCountryByCode3(code3);

    if (!country) {
      return null;
    }

    return country.zip ? country.zip : "generic";
  }

  public getZipRegex(code3: string): RegExp | null {
    const country = this.getCountryByCode3(code3);

    if (country && country.zip_regex) {
      return country.zip_regex;
    }

    return null;
  }

  public getZipKeyboard(code3: string): string | null {
    const country = this.getCountryByCode3(code3);

    if (country && country.zip_keyboard) {
      return country.zip_keyboard;
    }

    return null;
  }

  public getRegionCodeFromPostalCode(
    country_iso3: string | undefined,
    postal_code: string | undefined
  ): string | null {
    if (!country_iso3 || !postal_code) {
      return null;
    }

    const regions = this.getRegions(country_iso3);
    const match =
      regions &&
      regions.find(region => {
        const regex_match =
          region.zip_regex && postal_code.match(region.zip_regex);
        if (regex_match) {
          return true;
        }

        const zip_range_match =
          region.zip_ranges &&
          region.zip_ranges.find(range => {
            const digits = Number(postal_code.replace(/[^0-9].*/, ""));
            return digits >= range.lower && digits <= range.upper;
          });
        if (zip_range_match) {
          return true;
        }

        return false;
      });

    if (match) {
      return match.value;
    }

    return null;
  }

  public getExtraBillingField(
    country_iso3: string | undefined,
    key: string
  ): GeoDataExtraBillingField | null {
    const country = this.getCountryByCode3(country_iso3);

    if (!country || !country.extra_billing_fields) {
      return null;
    }

    const field = country.extra_billing_fields.find(field => field.key === key);

    if (!field) {
      return null;
    }

    return field;
  }

  public getBillingIDType(country_iso3: string | undefined): string | null {
    const country = this.getCountryByCode3(country_iso3);

    if (!country || !country.billing_id_type) {
      return null;
    }

    return country.billing_id_type;
  }

  public getFlagEmoji(iso3: string, device: DeviceInfo): string {
    if (!device.is_mobile) {
      return "";
    }

    if (!this.flag_emojis_map) {
      this.flag_emojis_map = [...this.data, ...MISSING_FLAG_EMOJIS].reduce(
        (emoji_map, { iso3, emoji = "" }) => {
          const emoji_codepoints = emoji
            .split(" ")
            .map(
              unicode => parseInt(unicode.replace(/U\+([0-9a-fA-F])/g, "0x$1")),
              16
            );

          return {
            ...emoji_map,
            [iso3]: emoji_codepoints
              ? String.fromCodePoint(...emoji_codepoints)
              : ""
          };
        },
        {}
      );
    }

    return (this.flag_emojis_map || {})[iso3] || "";
  }

  public getPhoneTrunkCode(iso3: string): string {
    if (!this.trunk_codes_map) {
      this.trunk_codes_map = this.data.reduce(
        (emoji_map, { iso3, trunk_code = "" }) => {
          return {
            ...emoji_map,
            [iso3]: trunk_code
          };
        },
        {}
      );
    }

    return (this.trunk_codes_map || {})[iso3] || "";
  }

  public getTopBillingCountries(
    locale: string,
    features: Partial<Features>
  ): Array<{ name: string; value: string }> {
    if (!this.top_billing_countries) {
      this.top_billing_countries = this.data
        .filter(({ iso3 }) =>
          getTopBillingCountriesRanks(features).includes(iso3)
        )
        .sort((a, b) => a.name.localeCompare(b.name, locale))
        .map(country => formatCountry(country));
    }

    return this.top_billing_countries;
  }

  public isUserCountryInTop(
    user_country: GeoDataCountry | undefined,
    features: Partial<Features>
  ): boolean {
    if (!user_country) {
      return false;
    }

    const top_billing_countries = getTopBillingCountriesRanks(features);
    return top_billing_countries.includes(user_country.iso3);
  }

  /**
   * Returns true if for a given country, we can automatically pre-check inputs to subscribe to marketing channels
   *
   * @param country_code2
   */
  public allowsImplicitConsentForMarketingEmails(
    country_code2: string
  ): boolean {
    if (!country_code2) {
      return false;
    }

    return COUNTRIES_ALLOWING_MARKETING_CHANNELS_IMPLICIT_CONSENT.includes(
      country_code2.toUpperCase()
    );
  }

  public isExplicitConsentRequiredForMarketingEmail(
    country_code2: string
  ): boolean {
    if (!country_code2) {
      return false;
    }

    return COUNTRIES_REQUIRING_MARKETING_CHANNELS_EXPLICIT_CONSENT.includes(
      country_code2.toUpperCase()
    );
  }
}

function formatCountry(country: GeoDataCountry): {
  name: string;
  value: string;
} {
  return {
    name: country.name,
    value: country.iso3
  };
}
